import { useState, useEffect } from 'react';
import { availabilityAPI, getCurrentUser } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

interface AvailabilitySlot {
  id?: number;
  day_of_week: number;
  day_name: string;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface TrainerSettings {
  buffer_between_sessions: number;
  advance_booking_days: number;
  cancellation_hours: number;
  min_session_duration: number;
  max_session_duration: number;
  auto_accept_bookings: boolean;
}

// Add the time conversion utility function
const convertTo12HourFormat = (time24: string): string => {
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  
  if (hour === 0) {
    return `12:${minutes} AM`;
  } else if (hour < 12) {
    return `${hour}:${minutes} AM`;
  } else if (hour === 12) {
    return `12:${minutes} PM`;
  } else {
    return `${hour - 12}:${minutes} PM`;
  }
};

export default function TrainerAvailabilityManager() {
  const { user } = useAuth();
  const [availability, setAvailability] = useState<AvailabilitySlot[]>([]);
  const [settings, setSettings] = useState<TrainerSettings>({
    buffer_between_sessions: 15,
    advance_booking_days: 30,
    cancellation_hours: 24,
    min_session_duration: 30,
    max_session_duration: 120,
    auto_accept_bookings: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const daysOfWeek = [
    { value: 0, label: 'Monday' },
    { value: 1, label: 'Tuesday' },
    { value: 2, label: 'Wednesday' },
    { value: 3, label: 'Thursday' },
    { value: 4, label: 'Friday' },
    { value: 5, label: 'Saturday' },
    { value: 6, label: 'Sunday' }
  ];

  const timeSlots: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 30) {
      const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      timeSlots.push(timeStr);
    }
  }

  useEffect(() => {
    if (user?.role === 'trainer') {
      fetchAvailability();
    }
  }, [user]);

  const fetchAvailability = async () => {
    if (!user?.id) return;
    try {
      const response = await availabilityAPI.getTrainerAvailability(user.id);
      const scheduleData = response.data.schedule || [];
      const convertedAvailability = scheduleData.map((slot: any) => ({
        id: slot.id,
        day_of_week: slot.day_of_week,
        day_name: slot.day_name,
        start_time: slot.start_time,
        end_time: slot.end_time,
        is_active: true
      }));
      
      setAvailability(convertedAvailability);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const addAvailabilitySlot = (dayOfWeek: number) => {
    const newSlot: AvailabilitySlot = {
      day_of_week: dayOfWeek,
      day_name: daysOfWeek.find(d => d.value === dayOfWeek)?.label || '',
      start_time: '09:00',
      end_time: '17:00',
      is_active: true
    };
    setAvailability([...availability, newSlot]);
  };

  const updateAvailabilitySlot = (index: number, field: keyof AvailabilitySlot, value: any) => {
    const updated = [...availability];
    updated[index] = { ...updated[index], [field]: value };
    setAvailability(updated);
  };

  const removeAvailabilitySlot = (index: number) => {
    setAvailability(availability.filter((_, i) => i !== index));
  };

  const saveAvailability = async () => {
    setSaving(true);
    try {
      console.log('Current settings before save:', settings);
      console.log('Availability slots being saved:', availability);
      
      const currentUser = getCurrentUser();

      if (!currentUser) {
        alert('Please log in to save availability.');
        return;
      }
      
      for (const slot of availability) {
        if (!slot.id) {
          // Create new slot
          console.log('Creating new slot:', slot);
          await availabilityAPI.addAvailabilitySlot({
            day_of_week: slot.day_of_week,
            start_time: slot.start_time,
            end_time: slot.end_time
          });
        } else {
          // Update existing slot
          console.log('Updating existing slot:', slot);
          await availabilityAPI.updateAvailabilitySlot(currentUser.id, slot.id.toString(), {
            day_of_week: slot.day_of_week,
            start_time: slot.start_time,
            end_time: slot.end_time
          });
        }
      }
      
      alert('Availability updated successfully!');
      await fetchAvailability();
    } catch (error) {
      console.error('Failed to save availability:', error);
      alert('Failed to save availability. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (user?.role !== 'trainer') {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center">
        <p className="text-gray-600">Only trainers can manage availability.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-300 rounded w-1/3"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-300 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Manage Your Availability</h1>
        <p className="text-gray-600">Set your weekly schedule and booking preferences</p>
      </div>

      {/* Weekly Availability */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Weekly Schedule</h2>
        
        {daysOfWeek.map(day => {
          const daySlots = availability.filter(slot => slot.day_of_week === day.value);
          
          return (
            <div key={day.value} className="mb-6 p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-900">{day.label}</h3>
                <button
                  onClick={() => addAvailabilitySlot(day.value)}
                  className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full hover:bg-green-200 transition-colors"
                >
                  Add Time Slot
                </button>
              </div>
              
              {daySlots.length === 0 ? (
                <p className="text-gray-500 text-sm italic">No availability set for this day</p>
              ) : (
                <div className="space-y-2">
                  {daySlots.map((slot, slotIndex) => {
                    const globalIndex = availability.findIndex(s => s === slot);
                    return (
                      <div key={slotIndex} className="flex items-center space-x-3">
                        {/* UPDATED: Show 12-hour format in dropdown */}
                        <select
                          value={slot.start_time}
                          onChange={(e) => updateAvailabilitySlot(globalIndex, 'start_time', e.target.value)}
                          className="flex-1 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        >
                          {timeSlots.map(time => (
                            <option key={time} value={time}>
                              {convertTo12HourFormat(time)}
                            </option>
                          ))}
                        </select>
                        
                        <span className="text-gray-500">to</span>
                        
                        {/* UPDATED: Show 12-hour format in dropdown */}
                        <select
                          value={slot.end_time}
                          onChange={(e) => updateAvailabilitySlot(globalIndex, 'end_time', e.target.value)}
                          className="flex-1 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        >
                          {timeSlots.map(time => (
                            <option key={time} value={time}>
                              {convertTo12HourFormat(time)}
                            </option>
                          ))}
                        </select>
                        
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={slot.is_active}
                            onChange={(e) => updateAvailabilitySlot(globalIndex, 'is_active', e.target.checked)}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Active</span>
                        </label>
                        
                        <button
                          onClick={() => removeAvailabilitySlot(globalIndex)}
                          className="text-red-600 hover:text-red-700 p-1"
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Booking Settings */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Booking Settings</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buffer Between Sessions (minutes)
            </label>
            <input
              type="number"
              value={settings.buffer_between_sessions}
              onChange={(e) => setSettings({...settings, buffer_between_sessions: parseInt(e.target.value)})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              min="0"
              max="60"
            />
            <p className="text-xs text-gray-500 mt-1">Time between sessions for travel/preparation</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Advance Booking (days)
            </label>
            <input
              type="number"
              value={settings.advance_booking_days}
              onChange={(e) => setSettings({...settings, advance_booking_days: parseInt(e.target.value)})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              min="1"
              max="90"
            />
            <p className="text-xs text-gray-500 mt-1">How far in advance customers can book</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cancellation Notice (hours)
            </label>
            <input
              type="number"
              value={settings.cancellation_hours}
              onChange={(e) => setSettings({...settings, cancellation_hours: parseInt(e.target.value)})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              min="1"
              max="72"
            />
            <p className="text-xs text-gray-500 mt-1">Minimum hours notice for cancellation</p>
          </div>
          
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.auto_accept_bookings}
                onChange={(e) => setSettings({...settings, auto_accept_bookings: e.target.checked})}
                className="mr-3"
              />
              <span className="text-sm font-medium text-gray-700">Auto-accept bookings</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">Automatically accept bookings within your availability</p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveAvailability}
          disabled={saving}
          className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Availability'}
        </button>
      </div>

      {/* UPDATED: Usage Example with 12-hour format */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg">
        <h3 className="font-medium text-blue-900 mb-2">Example Schedule</h3>
        <p className="text-sm text-blue-800 mb-2">
          A trainer who can't work before 8am, takes lunch 1-2pm, and stops at 9pm would set:
        </p>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Monday-Friday: 8:00 AM to 1:00 PM, then 2:00 PM to 9:00 PM</li>
          <li>• Saturday: 9:00 AM to 3:00 PM</li>
          <li>• Sunday: No availability (don't add any slots)</li>
        </ul>
      </div>
    </div>
  );
}